import React, {useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLocation} from 'react-router-dom';
import moment from 'moment';
import {
  RedBannerUnderlineText,
  RedBannerWrapper
} from 'components/RedBannersStoreSettigs/styles';
import {IconUnPublish} from 'assets/IconUnPublish';
import Body3 from 'components/typography/Body3';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {DELETE_SPECIAL_HOURS} from 'store/global/user/action';
import {useBannerSlot} from 'components/BannerStack/BannerStackContext';
import BANNER_STACK_PRIORITY from 'components/BannerStack/priorities';

const formatTimeRange = (start, end) => {
  const startHour = start?.hour ?? start?.Hour;
  const startMinute = start?.minute ?? start?.Minute ?? 0;
  const endHour = end?.hour ?? end?.Hour;
  const endMinute = end?.minute ?? end?.Minute ?? 0;

  if (startHour == null || endHour == null) return '';

  const s = moment().set({
    hour: startHour,
    minute: startMinute,
    second: 0,
    millisecond: 0
  });
  const e = moment().set({
    hour: endHour,
    minute: endMinute,
    second: 0,
    millisecond: 0
  });
  return `${s.format('h:mm A')} - ${e.format('h:mm A')}`;
};

const parseSpecialHourDate = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const dateOnly = value.includes('T') ? value.split('T')[0] : value;
    const strictDate = moment(dateOnly, 'YYYY-MM-DD', true);
    if (strictDate.isValid()) return strictDate;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

const getNearestSpecialHoursEntry = (specialHours) => {
  if (!Array.isArray(specialHours) || specialHours.length === 0) return null;
  const today = moment().startOf('day');
  const futureOrCurrent = specialHours
    .filter((item) => {
      const endsOn = item.endsOn || item.EndsOn;
      const endDate = parseSpecialHourDate(endsOn);
      if (!endDate) return false;
      return endDate.endOf('day').isSameOrAfter(today);
    })
    .slice()
    .sort((a, b) => {
      const aStart = a.startsOn || a.StartsOn;
      const bStart = b.startsOn || b.StartsOn;
      const aStartDate = parseSpecialHourDate(aStart);
      const bStartDate = parseSpecialHourDate(bStart);
      if (!aStartDate && !bStartDate) return 0;
      if (!aStartDate) return 1;
      if (!bStartDate) return -1;
      return aStartDate.valueOf() - bStartDate.valueOf();
    });
  return futureOrCurrent[0] || null;
};

const getBannerText = (entry) => {
  if (!entry) return '';
  const rawStart = entry.startsOn || entry.StartsOn;
  const rawEnd = entry.endsOn || entry.EndsOn;
  if (!rawStart || !rawEnd) return '';

  const startMoment = parseSpecialHourDate(rawStart);
  const endMoment = parseSpecialHourDate(rawEnd);
  if (!startMoment || !endMoment) return '';
  const startFormatted = startMoment.format('MM/DD/YYYY');
  const endFormatted = endMoment.format('MM/DD/YYYY');
  const isOneDay = startMoment.isSame(endMoment, 'day');

  const type = entry.type || entry.Type;

  if (type === 'closing_hours') {
    if (isOneDay) {
      return `The store will not receive orders on ${startFormatted}`;
    }
    return `The store will not receive orders from ${startFormatted} to ${endFormatted}`;
  }

  const times = entry.times || entry.Times || entry.hours || entry.Hours;

  if (type === 'opening_hours' && times?.length) {
    const timeRangesStr = times
      .map((t) => formatTimeRange(t.start || t.Start, t.end || t.End))
      .filter(Boolean)
      .join(', ');
    if (isOneDay) {
      return `The store will be open ${timeRangesStr} on ${startFormatted}`;
    }
    return `The store will be open ${timeRangesStr} from ${startFormatted} to ${endFormatted}`;
  }

  if (isOneDay) {
    return `Special hours are active on ${startFormatted}`;
  }
  return `Special hours are active from ${startFormatted} to ${endFormatted}`;
};

const RedBannerSpecialHours = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const activeLocation = useSelector((state) => state.user.activeLocation);
  const allowed = activeLocation?.AllowedServiceFeatures?.includes(
    FEATURE_RESTRICTION_IDS.HOLIDAY_HOURS_V2
  );

  const specialHours = activeLocation?.SpecialHours;

  const entry = useMemo(() => getNearestSpecialHoursEntry(specialHours), [
    specialHours
  ]);

  const text = useMemo(() => getBannerText(entry), [entry]);

  const isActive = Boolean(allowed && entry && text);
  const stackIndex = useBannerSlot(
    'storeSpecialHours',
    BANNER_STACK_PRIORITY.storeSpecialHours,
    isActive
  );

  if (!isActive) return null;

  return (
    <RedBannerWrapper
      className='RedBannerSpecialHours'
      style={{'--banner-stack-index': stackIndex}}
    >
      <IconUnPublish />
      <Body3 className='textRedBanner White'>
        {text}{' '}
        {location?.pathname !== '/store-settings' && (
          <RedBannerUnderlineText
            onClick={() => dispatch(DELETE_SPECIAL_HOURS(entry.id || entry.Id))}
          >
            Click to cancel
          </RedBannerUnderlineText>
        )}
      </Body3>
    </RedBannerWrapper>
  );
};

export default RedBannerSpecialHours;
